import { useState } from "react";
import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import DonateButton, { useDonationCampaign, markDonationIntent } from "../components/site/DonateButton";
import JustGivingCheckout from "../components/site/JustGivingCheckout";
import { api, formatApiError } from "../lib/api";
import { IMAGES } from "../lib/images";
import { useReveal } from "../lib/useReveal";
import { ShieldCheck, Lock, ArrowUpRight } from "lucide-react";

const AMOUNTS = [
  { value: "£10", note: "a helping hand with everyday essentials" },
  { value: "£25", note: "practical support for a family facing hardship" },
  { value: "£50", note: "a meaningful contribution to community projects" },
  { value: "£100", note: "a generous gift towards sustained assistance" },
];

function InlineNewsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      await api.post("/newsletter/subscribe", { email, website: "" });
      setState({ status: "success", message: "Thank you — please check your inbox for a confirmation link to activate your subscription." });
      setEmail("");
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };
  return (
    <div className="bg-white border border-sandstone p-8 lg:p-10">
      <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-forest">Stay connected</h2>
      <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">
        Optionally receive updates about our work, community projects and the difference your
        support makes. Subscribing is entirely voluntary — no account, no password — and you can
        unsubscribe at any time. Donating never subscribes you automatically.
      </p>
      {state.status === "success" ? (
        <p className="mt-5 text-sm text-forest font-medium" data-testid="donate-newsletter-success">{state.message}</p>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3">
          <label htmlFor="donate-nl-email" className="sr-only">Enter your email address</label>
          <input
            id="donate-nl-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            data-testid="donate-newsletter-email-input"
            className="flex-1 border border-sage bg-white px-4 py-3 text-sm focus:border-forest focus:ring-1 focus:ring-forest rounded-sm"
          />
          <button type="submit" disabled={state.status === "loading"}
            data-testid="donate-newsletter-subscribe-button"
            className="bg-forest text-white font-medium px-6 py-3 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
            {state.status === "loading" ? "Subscribing…" : "Subscribe to Updates"}
          </button>
        </form>
      )}
      {state.status === "error" && <p className="mt-3 text-sm text-red-700" role="alert">{state.message}</p>}
    </div>
  );
}

export default function Donate() {
  const ref = useReveal();
  const cfg = useDonationCampaign();
  return (
    <div ref={ref}>
      <Seo
        title="Donate"
        path="/donate"
        description="Support Mount Moriah Relief through our official JustGiving page. Donations are processed securely by JustGiving — we never handle your card details."
      />
      <PageHero
        eyebrow="Donate"
        title="Your Compassion Can Make a Difference."
        image={IMAGES.heroZimbabwe.src}
        imageAlt={IMAGES.heroZimbabwe.alt}
      >
        <p>
          Every gift helps prevent and relieve poverty, hardship and suffering — through
          humanitarian assistance, grants, essential goods, services and responsible partnerships.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 reveal">
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
              Give through our official JustGiving page
            </h2>
            <div className="mt-6 space-y-5 text-charcoal/80 leading-relaxed">
              <p>
                Mount Moriah Relief receives online donations through JustGiving. When you give, the
                entire payment is processed on JustGiving's secure platform — your card details are
                never collected, seen or stored by this website.
              </p>
              <p>
                Contributions support our charitable purposes: humanitarian assistance, grants,
                essential goods and services for people facing hardship, delivered in collaboration
                with trusted partners — with a particular focus on vulnerable communities in
                Zimbabwe.
              </p>
            </div>

            <h3 className="mt-10 font-serif text-2xl font-semibold text-charcoal">How giving works</h3>
            <ol className="mt-5 space-y-4 text-sm text-charcoal/80 leading-relaxed list-none">
              {[
                "Choose the donate button below — you will be taken to our official page on JustGiving.",
                "Enter your chosen amount and payment details on JustGiving's secure checkout.",
                "JustGiving processes the payment and transfers funds to the organisation through its verified payment procedures.",
                "You will receive a receipt directly from JustGiving.",
              ].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex items-center justify-center w-7 h-7 shrink-0 bg-forest text-white text-xs font-semibold">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>

            <div className="mt-10">
              <JustGivingCheckout />
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-sandstone/50 border border-sandstone p-5">
                <Lock size={20} strokeWidth={1.5} className="text-forest shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-charcoal/75 leading-relaxed">
                  <strong className="text-charcoal">Secure by design.</strong> We never ask for card
                  numbers, CVV codes or banking logins on this website.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-sandstone/50 border border-sandstone p-5">
                <ShieldCheck size={20} strokeWidth={1.5} className="text-forest shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-charcoal/75 leading-relaxed">
                  <strong className="text-charcoal">Transparent by principle.</strong> Fundraising
                  progress shown anywhere on this site comes only from verified sources.
                </p>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5 space-y-8 reveal">
            <div className="bg-white border border-sandstone p-8">
              <h3 className="font-serif text-2xl font-semibold text-forest">Suggested amounts</h3>
              <p className="mt-2 text-xs text-charcoal/60">Shown for illustration — every gift of any size is valued.</p>
              <ul className="mt-6 divide-y divide-sandstone">
                {AMOUNTS.map((a) => (
                  <li key={a.value} className="py-4 flex items-baseline gap-4">
                    <span className="font-serif text-2xl font-semibold text-forest w-20">{a.value}</span>
                    <span className="text-sm text-charcoal/75">{a.note}</span>
                  </li>
                ))}
              </ul>
              {cfg.campaign_configured && cfg.campaign_url && (
                <a
                  href={cfg.campaign_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={markDonationIntent}
                  className="mt-4 inline-flex items-center gap-2 text-sm text-forest font-medium hover:underline"
                  data-testid="donate-choose-amount-link"
                >
                  Choose your amount on JustGiving <ArrowUpRight size={14} aria-hidden="true" />
                </a>
              )}
            </div>
            <InlineNewsletter />
          </aside>
        </div>
      </section>
    </div>
  );
}
